import React, { useState, useEffect } from 'react';
import { cn } from '../../utils/cn';

const BottomNavigation = ({ 
  items = [],
  activeItem,
  onItemClick,
  className = '',
  position = 'fixed' // fixed, sticky, static
}) => {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [hoveredItem, setHoveredItem] = useState(null);

  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  const handleItemClick = (item, index) => {
    if (onItemClick) {
      onItemClick(item, index);
    }
  };

  const handleItemHover = (item, index) => {
    setHoveredItem(index);
  };

  const handleItemLeave = () => {
    setHoveredItem(null);
  };

  return (
    <nav
      className={cn(
        'bottom-0 left-0 right-0 z-50 bg-black/0 backdrop-blur-0 border-t border-white/0',
        position === 'fixed' && 'fixed',
        position === 'sticky' && 'sticky',
        position === 'static' && 'relative',
        className
      )}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-center py-3">
          <div className="flex items-center space-x-1 z-50 bg-blue-600/100 rounded-full p-2 backdrop-blur-sm">
            {items.map((item, index) => {
              const isActive = activeItem === item.id || activeItem === index;
              const isHovered = hoveredItem === index;
              
              return (
                <button
                  key={item.id || index}
                  onClick={() => handleItemClick(item, index)}
                  onMouseEnter={() => handleItemHover(item, index)}
                  onMouseLeave={handleItemLeave}
                  className={cn(
                    'relative flex items-center justify-center px-4 py-2 rounded-full transition-all duration-300 ease-out group',
                    'hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-white/20',
                    isActive && 'bg-white text-black',
                    !isActive && 'text-white/70 hover:text-white'
                  )}
                  aria-label={item.label}
                >
                  {/* Icon */}
                  <div className={cn(
                    'flex items-center justify-center transition-all duration-300',
                    isActive && 'mr-2',
                    !isActive && item.label && isHovered && 'mr-2'
                  )}>
                    {item.icon && (
                      <item.icon 
                        className={cn(
                          'transition-all duration-300',
                          isActive ? 'h-4 w-4 text-primary' : 'h-5 w-5'
                        )} 
                      />
                    )}
                  </div>
                  
                  {/* Label */}
                  <span className={cn(
                    'text-sm font-medium transition-all duration-300 overflow-hidden whitespace-nowrap',
                    isActive ? 'max-w-[100px] opacity-100 text-primary' : 'max-w-0 opacity-0',
                    !isActive && item.label && isHovered && 'max-w-[100px] opacity-100'
                  )}>
                    {item.label}
                  </span>
                  
                  {/* Badge */}
                  {item.badge && (
                    <div className={cn(
                      'absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center',
                      'animate-pulse'
                    )}>
                      {item.badge}
                    </div>
                  )}
                  
                  {/* Hover dot indicator */}
                  {!isActive && isHovered && (
                    <div 
                      className="absolute inset-0 rounded-full bg-gradient-to-r from-blue-500/20 to-purple-500/20 animate-pulse"
                      style={{
                        background: `radial-gradient(circle at ${mousePosition.x}px ${mousePosition.y}px, rgba(59, 130, 246, 0.1) 0%, transparent 50%)`
                      }}
                    />
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </div>
      
      {/* Ambient glow effect */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent pointer-events-none" />
    </nav>
  );
};

export default BottomNavigation;